import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  MapPin, Route, Briefcase, Sparkles, GraduationCap, Stethoscope, Building2, ArrowRight,
} from "lucide-react";
import { apiGet } from "@/lib/api";
import { useSEO, graph, breadcrumbJsonLd, faqJsonLd } from "@/lib/seo";
import { trackEvent } from "@/lib/tracking";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import PropertyCard from "@/components/common/PropertyCard";
import ProjectCard from "@/components/common/ProjectCard";
import LeadForm from "@/components/common/LeadForm";
import { Breadcrumbs, ErrorState, SectionLabel } from "@/components/common/states";

const INFO = [
  { key: "connectivity", icon: Route, title: "Connectivity" },
  { key: "employment_hubs", icon: Briefcase, title: "Employment Hubs", list: true },
  { key: "lifestyle", icon: Sparkles, title: "Lifestyle" },
  { key: "education", icon: GraduationCap, title: "Education" },
  { key: "healthcare", icon: Stethoscope, title: "Healthcare" },
];

export default function LocationDetail() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setD(null); setError(false);
    apiGet(`/locations/${slug}`).then((r) => { setD(r); trackEvent("view_location", { location: slug, page_type: "location" }); }).catch(() => setError(true));
  }, [slug]);

  const loc = d?.location;
  useSEO(loc ? {
    title: loc.seo_title?.replace(" | Endeavour Castle", "") || `Property in ${loc.name}, Pune`,
    description: loc.seo_description, path: `/locations/${loc.slug}`, image: loc.image,
    jsonLd: graph(
      { "@type": "Place", name: `${loc.name}, Pune`, description: loc.description, ...(loc.image ? { image: loc.image } : {}) },
      breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Locations", path: "/locations" }, { name: loc.name }]),
      loc.faqs?.length ? faqJsonLd(loc.faqs) : null,
    ),
  } : { title: "Location" });

  if (error) return <div className="ec-container py-20"><ErrorState /></div>;
  if (!d) return <div className="ec-container py-20 text-center text-muted-foreground">Loading location…</div>;

  return (
    <div>
      <section className="relative h-[46vh] min-h-[340px]">
        <img src={loc.image} alt={`${loc.name}, Pune`} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />
        <div className="relative ec-container h-full flex flex-col justify-end pb-8">
          <div className="mb-4"><Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Locations", to: "/locations" }, { label: loc.name }]} /></div>
          <p className="text-accent text-sm font-semibold uppercase tracking-[0.2em]">Pune</p>
          <h1 className="font-display text-4xl sm:text-5xl text-white mt-1">{loc.name}</h1>
        </div>
      </section>

      <div className="ec-container mt-12 grid lg:grid-cols-[1fr_360px] gap-12">
        <div className="min-w-0">
          <SectionLabel>Overview</SectionLabel>
          <p className="text-muted-foreground leading-relaxed text-lg">{loc.description}</p>

          {loc.why_choose?.length > 0 && (
            <div className="mt-8">
              <h2 className="font-display text-2xl text-foreground mb-4">Why people choose {loc.name}</h2>
              <div className="grid sm:grid-cols-3 gap-4">
                {loc.why_choose.map((w, i) => (
                  <div key={i} className="p-5 rounded-md border border-border bg-white text-sm text-foreground/80">{w}</div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {INFO.map((info) => {
              const val = loc[info.key];
              if (!val || (Array.isArray(val) && val.length === 0)) return null;
              return (
                <div key={info.key} className="p-5 rounded-md border border-border bg-white">
                  <h3 className="flex items-center gap-2 font-medium text-foreground mb-2"><info.icon className="h-4 w-4 text-primary" aria-hidden /> {info.title}</h3>
                  {info.list ? (
                    <ul className="text-sm text-muted-foreground space-y-1">{val.map((x, i) => <li key={i}>· {x}</li>)}</ul>
                  ) : (
                    <p className="text-sm text-muted-foreground leading-relaxed">{val}</p>
                  )}
                </div>
              );
            })}
          </div>

          {d.projects?.length > 0 && (
            <div className="mt-12">
              <h2 className="font-display text-2xl text-foreground mb-5">Featured Projects in {loc.name}</h2>
              <div className="grid sm:grid-cols-2 gap-6">{d.projects.slice(0, 4).map((p, i) => <ProjectCard key={p.slug} project={p} index={i} />)}</div>
            </div>
          )}

          <div className="mt-12">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-2xl text-foreground">Available Properties in {loc.name}</h2>
              <Link to={`/properties?location=${loc.slug}`} className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-primary">View all <ArrowRight className="h-4 w-4" aria-hidden /></Link>
            </div>
            {d.properties?.length > 0 ? (
              <div className="grid sm:grid-cols-2 gap-6">{d.properties.slice(0, 4).map((p, i) => <PropertyCard key={p.slug} property={p} index={i} />)}</div>
            ) : (
              <div className="p-8 rounded-md border border-border bg-white text-center text-muted-foreground">
                No listings published for {loc.name} yet. <Link to="/requirements" className="text-primary underline underline-offset-4">Tell us your requirement</Link> and we'll help you find options.
              </div>
            )}
          </div>

          {loc.faqs?.length > 0 && (
            <div className="mt-12">
              <h2 className="font-display text-2xl text-foreground mb-4">FAQs about {loc.name}</h2>
              <Accordion type="single" collapsible>
                {loc.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`f${i}`}>
                    <AccordionTrigger className="text-left text-foreground">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          {loc.nearby?.length > 0 && (
            <div className="mt-12" data-testid="nearby-localities">
              <h2 className="font-display text-2xl text-foreground mb-4">Explore nearby localities</h2>
              <div className="flex flex-wrap gap-3">
                {loc.nearby.map((n) => (
                  <Link key={n.slug} to={`/locations/${n.slug}`} data-testid={`nearby-${n.slug}`}
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-medium text-foreground hover:border-primary hover:text-primary transition-colors">
                    <MapPin className="h-4 w-4" aria-hidden /> Property in {n.name}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside>
          <div className="lg:sticky lg:top-28 rounded-lg border border-border bg-white p-6">
            <SectionLabel>Enquire</SectionLabel>
            <h3 className="font-display text-xl text-foreground mb-4">Find Property in {loc.name}</h3>
            <LeadForm source="location" variant="contact" compact submitLabel="Get Options"
              defaults={{ location: loc.name, message: `I'm interested in property in ${loc.name}, Pune.` }} />
          </div>
        </aside>
      </div>
      <div className="h-16" />
    </div>
  );
}
